import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type Counts = Record<string, number>;

let cachedCounts: Counts | null = null;
const subscribers = new Set<(counts: Counts) => void>();

async function loadCounts() {
  const { data, error } = await supabase
    .from("download_counts")
    .select("slug, count");
  if (error) {
    console.error("Failed to load download counts", error);
    return;
  }
  const next: Counts = {};
  for (const row of data ?? []) {
    next[row.slug as string] = Number(row.count);
  }
  cachedCounts = next;
  subscribers.forEach((cb) => cb(next));
}

let realtimeSetup = false;
function setupRealtime() {
  if (realtimeSetup) return;
  realtimeSetup = true;
  supabase
    .channel("download_counts_changes")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "download_counts" },
      (payload) => {
        if (!cachedCounts) cachedCounts = {};
        const row = (payload.new ?? payload.old) as { slug: string; count: number } | null;
        if (row && row.slug) {
          cachedCounts = { ...cachedCounts, [row.slug]: Number(row.count) };
          subscribers.forEach((cb) => cb(cachedCounts!));
        }
      }
    )
    .subscribe();
}

export function useDownloadCounts() {
  const [counts, setCounts] = useState<Counts>(cachedCounts ?? {});

  useEffect(() => {
    subscribers.add(setCounts);
    if (!cachedCounts) {
      loadCounts();
    }
    setupRealtime();
    return () => {
      subscribers.delete(setCounts);
    };
  }, []);

  const increment = useCallback(async (slug: string) => {
    // optimistic
    const optimistic = { ...(cachedCounts ?? {}), [slug]: (cachedCounts?.[slug] ?? 0) + 1 };
    cachedCounts = optimistic;
    subscribers.forEach((cb) => cb(optimistic));

    const { data, error } = await supabase.rpc("increment_download", { _slug: slug });
    if (error) {
      console.error("increment_download failed", error);
      return;
    }
    if (typeof data === "number" || typeof data === "string") {
      const newCount = Number(data);
      cachedCounts = { ...(cachedCounts ?? {}), [slug]: newCount };
      subscribers.forEach((cb) => cb(cachedCounts!));
    }
  }, []);

  return { counts, increment };
}
